import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';
import { applyActionCode } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../api/firebase/config';
import {
  gray80,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
  white,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import MetaTitle from '../../utils/MetaTitle';
import logoImage from '../../assets/logo_color.png';

const CONFIRM_MESSAGE =
  'お客様のアカウントに認証アプリ（二段階認証）が登録されました。\nこの操作に心当たりがない場合は、取り消してください。';

const WARNING_MESSAGE =
  '心当たりがない場合、パスワードが第三者に知られている可能性があります。取り消したあとにパスワードを変更してください。';

const COMPLETED_MESSAGE =
  '認証アプリ（二段階認証）の登録を取り消しました。\n第三者による登録の可能性があるため、続けてパスワードを再設定してください。';

const INVALID_CODE_MESSAGE =
  'このリンクは無効か、有効期限が切れています。すでに取り消し済みの場合もこの表示になります。';

const styles = {
  // 設定要求画面（MfaSetupRequired.tsx）と同じ構成: 白背景・公式カラーロゴを中央配置
  container: {
    alignItems: 'center',
    backgroundColor: white,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh',
    px: '24px',
    py: '48px',
    width: '100%',
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '560px',
    textAlign: 'center',
    width: '100%',
  },
  logo: {
    height: 'auto',
    width: '300px',
    '@media screen and (max-width:600px)': {
      width: '240px',
    },
  },
  message: {
    color: gray80,
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: 1.8,
    mt: '40px',
    whiteSpace: 'pre-wrap',
    '@media screen and (max-width:600px)': {
      fontSize: '16px',
      mt: '32px',
    },
  },
  warningBox: {
    backgroundColor: yellow_overlay10,
    border: `1px solid ${yellow50}`,
    borderRadius: '4px',
    color: yellow50,
    fontSize: '14px',
    lineHeight: 1.8,
    mt: '24px',
    p: '12px 16px',
    textAlign: 'left',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    lineHeight: 1.8,
    mt: '24px',
    p: '12px 16px',
    textAlign: 'left',
  },
  actionButton: {
    height: '44px',
    minWidth: '200px',
    mt: '32px',
    '@media screen and (max-width:600px)': {
      mt: '24px',
      width: '100%',
    },
  },
};

/**
 * 二段階認証（TOTP）の登録取り消し画面（#3300）。
 *
 * Firebase が TOTP 登録時に自動送信する通知メールの
 * `mode=revertSecondFactorAddition` リンクの遷移先。
 *
 * マイページの解除ダイアログ（MfaUnenrollDialog）とは用途が異なる。あちらは
 * 認証アプリを持つ本人がログイン後に解除するもので、再認証時に確認コードを要求する。
 * 対してこの画面は「第三者に勝手に登録され、認証アプリを持っていない被害者」が
 * ログインできない状態で取り消すためのもので、oobCode だけで実行する。
 *
 * メールクライアントのリンク先読みによる意図しない取り消しを防ぐため、
 * 遷移しただけでは実行せず、ボタンの明示的な操作を挟む。
 */
const MfaRevert: React.FC = () => {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const oobCode = queryParams.get('oobCode') ?? '';
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleRevert = async (): Promise<void> => {
    setSubmitting(true);
    setErrorMessage('');
    try {
      await applyActionCode(auth, oobCode);
      // applyActionCode はサーバー側の第二要素を削除するが、既にログイン済みの
      // セッションが持つ ID トークンは自動更新されない。enrolledFactors は
      // そのトークンのキャッシュを見るため、更新しないとマイページの表示が
      // 「有効」のまま残る（#3300）
      await auth.currentUser?.getIdToken(true);
      setCompleted(true);
    } catch (e) {
      // 無効・期限切れ・使用済みはユーザーの取るべき行動が同じ（リンクが使えない）ため
      // 文言を分けない。想定外のエラーは Sentry に送って調査できるようにする
      if (!(e instanceof FirebaseError)) {
        Sentry.captureException(e);
      }
      setErrorMessage(INVALID_CODE_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MetaTitle customTitle="二段階認証の登録を取り消す" />
      <Box sx={styles.container}>
        <Box sx={styles.content}>
          <Box component="img" src={logoImage} alt="Gen Drive" sx={styles.logo} />

          {completed ? (
            <>
              <Typography component="h1" sx={styles.message}>
                {COMPLETED_MESSAGE}
              </Typography>
              <Button
                onClick={(): void => navigate('/auth/password_reset')}
                sx={[primaryButtonBackgroundColor, styles.actionButton]}
                variant="contained"
              >
                パスワードを再設定する
              </Button>
            </>
          ) : (
            <>
              <Typography component="h1" sx={styles.message}>
                {CONFIRM_MESSAGE}
              </Typography>
              <Box sx={styles.warningBox}>{WARNING_MESSAGE}</Box>
              {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}
              <Button
                onClick={handleRevert}
                disabled={submitting || !oobCode}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={[primaryButtonBackgroundColor, styles.actionButton]}
                variant="contained"
              >
                取り消す
              </Button>
              <Button
                onClick={(): void => navigate('/')}
                sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
              >
                心当たりがあるため、何もしない
              </Button>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default MfaRevert;
